import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useAuth } from '@/contexts/AuthContext';
import { SI_PORTAL_BASE_PATH, abaEmBreve, getSiPortalTabHref } from '../siPortal.definitions';
import type { SiPortalTabId } from '../siPortal.types';
import { useSiPortalTabs } from '../hooks/useSiPortalTabs';
import { SI_PORTAL_TOUR } from './siPortalTour.definitions';
import { useSiPortalTour } from './useSiPortalTour';

const CHAVE_FILA = 'si-portal-tour-fila';

function lerFila(): SiPortalTabId[] {
  try {
    return JSON.parse(sessionStorage.getItem(CHAVE_FILA) ?? '[]') as SiPortalTabId[];
  } catch {
    return [];
  }
}

function gravarFila(fila: SiPortalTabId[]) {
  try {
    if (fila.length) sessionStorage.setItem(CHAVE_FILA, JSON.stringify(fila));
    else sessionStorage.removeItem(CHAVE_FILA);
  } catch {
    // sem storage: o "Conhecer o portal" para na aba atual
  }
}

/** Aba do portal na URL (/si/<aba> ou /si/documentos/:id); telas internas (post aberto etc.) não têm tour. */
function abaDaUrl(pathname: string): SiPortalTabId | null {
  const [, base, segmento, resto] = pathname.split('/');
  if (`/${base}` !== SI_PORTAL_BASE_PATH || !segmento) return null;
  if (resto && segmento !== 'documentos') return null;
  return segmento as SiPortalTabId;
}

/**
 * Botão "?" do cabeçalho do portal: "Tour desta aba" e "Conhecer o portal (todas as abas)" —
 * este passa pelas abas que a pessoa vê, em ordem (navega sozinho entre elas). Na primeira
 * visita o "?" pisca convidando, sem abrir sozinho.
 */
export function SiPortalAjuda() {
  const { user, userHasRole } = useAuth();
  const tabs = useSiPortalTabs();
  const location = useLocation();
  const navigate = useNavigate();
  const { iniciar, jaViu } = useSiPortalTour(user?.email);
  const [menuAberto, setMenuAberto] = useState(false);
  const [piscar, setPiscar] = useState(() => !jaViu());
  const menuRef = useRef<HTMLDivElement>(null);
  const abaAtual = abaDaUrl(location.pathname);

  const abasComTour = tabs
    .filter((t) => !abaEmBreve(t, { hasRole: userHasRole }) && (SI_PORTAL_TOUR[t.id]?.length ?? 0) > 0)
    .map((t) => t.id);

  const tourDaAba = (aba: SiPortalTabId, fila: SiPortalTabId[] = []) => {
    setPiscar(false);
    void iniciar(SI_PORTAL_TOUR[aba] ?? [], {
      aoConcluir: () => {
        const [proxima, ...depois] = fila;
        gravarFila(proxima ? [proxima, ...depois] : []);
        const destino = tabs.find((t) => t.id === proxima);
        if (destino) navigate(getSiPortalTabHref(destino));
      },
      aoSair: () => gravarFila([]),
    });
  };

  // "Conhecer o portal": ao chegar na próxima aba da fila, segue o tour dela.
  useEffect(() => {
    const fila = lerFila();
    if (fila.length && abaAtual && fila[0] === abaAtual) {
      const timer = setTimeout(() => tourDaAba(abaAtual, fila.slice(1)), 400);
      return () => clearTimeout(timer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (!menuAberto) return undefined;
    const fechar = (evento: MouseEvent) => {
      if (!menuRef.current?.contains(evento.target as Node)) setMenuAberto(false);
    };
    document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, [menuAberto]);

  const conhecerPortal = () => {
    setMenuAberto(false);
    const [primeira, ...resto] = abasComTour;
    if (!primeira) return;
    if (primeira === abaAtual) {
      gravarFila([]);
      tourDaAba(primeira, resto);
    } else {
      gravarFila([primeira, ...resto]);
      const destino = tabs.find((t) => t.id === primeira);
      if (destino) navigate(getSiPortalTabHref(destino));
    }
  };

  const temTourAqui = Boolean(abaAtual && SI_PORTAL_TOUR[abaAtual]?.length);

  return (
    <div className="relative shrink-0" ref={menuRef}>
      <button
        type="button"
        data-tour="si-ajuda"
        onClick={() => setMenuAberto((atual) => !atual)}
        aria-label="Ajuda"
        aria-expanded={menuAberto}
        title="Ajuda"
        className={clsx(
          'grid h-9 w-9 place-items-center rounded-full bg-white font-montserrat text-lg font-bold text-primary-600 shadow-sm ring-1 ring-primary-100 transition-colors hover:bg-primary-50',
          piscar && 'animate-pulse-raro'
        )}
      >
        ?
      </button>
      {menuAberto && (
        <div className="absolute right-0 top-full z-40 mt-2 w-64 overflow-hidden rounded-xl border border-raro-gray-200 bg-white text-sm shadow-lg">
          <button
            type="button"
            disabled={!temTourAqui}
            onClick={() => {
              setMenuAberto(false);
              if (abaAtual) tourDaAba(abaAtual);
            }}
            className="block w-full border-b border-raro-gray-100 px-4 py-3 text-left font-medium text-raro-gray-700 hover:bg-primary-50 disabled:cursor-not-allowed disabled:text-raro-gray-400"
          >
            🧭 Tour desta aba
          </button>
          <button
            type="button"
            onClick={conhecerPortal}
            className="block w-full px-4 py-3 text-left font-medium text-raro-gray-700 hover:bg-primary-50"
          >
            🗺️ Conhecer o portal (todas as abas)
          </button>
        </div>
      )}
    </div>
  );
}
